import { Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { HeaderCard } from '@/components/layout/HeaderCard';
import { Callout } from '@/components/notifications/Callout/Callout';
import { Loader } from '@/components/progress/Loader/Loader';
import { useReportError } from '@/hooks/useReportError';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { TenantOrgMembershipType } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { CreateOrganizationModal } from './CreateOrganizationModal';
import { useAdminOrganizationsPageQuery } from './graphql/AdminOrganizationsPage.generated';
import { OrganizationDetails } from './OrganizationDetails';

export function AdminOrganizationsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Undefined means follow the active tenant's organization. A selected id is
  // an explicit cross-organization view, including from a standalone tenant.
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | undefined
  >();
  const {
    isTenantInformationLoaded,
    luminaryOrganizationID: currentOrganizationId,
    orgMembershipType,
  } = useTenantDetailsContext();
  const { data, loading, error } = useAdminOrganizationsPageQuery({
    fetchPolicy: 'cache-and-network',
  });
  useReportError('Failed to load organizations admin page', error);

  const organizations = useMemo(
    () =>
      [...getNodes(data?.luminaryOrganizations)].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      ),
    [data?.luminaryOrganizations]
  );
  const tenants = useMemo(
    () => data?.adminListTenants ?? [],
    [data?.adminListTenants]
  );
  const organizationId = selectedOrganizationId ?? currentOrganizationId;
  const selectedOrganization = organizations.find(
    (organization) => organization.id === organizationId
  );
  const canEdit =
    isTenantInformationLoaded &&
    orgMembershipType === TenantOrgMembershipType.Parent &&
    selectedOrganization?.id === currentOrganizationId;
  const hasMoreOrganizations =
    data?.luminaryOrganizations.pageInfo.hasNextPage ?? false;
  const organizationTotalCount = data?.luminaryOrganizations.totalCount ?? 0;
  const organizationOptions = organizations.map((organization) => ({
    display: organization.displayName,
    value: organization.id,
  }));

  return (
    <SubpageLayout
      heading="Organizations"
      actions={
        <Button
          variant="primary"
          size="sm"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create organization
        </Button>
      }
    >
      <Stack spacing={2}>
        {hasMoreOrganizations && (
          <Callout severity="warning">
            Showing {organizations.length} of {organizationTotalCount}{' '}
            organizations. Some organizations are not available in the selector.
          </Callout>
        )}

        <Stack maxWidth={500}>
          <TypeaheadSelectInput<string>
            label="View organization"
            name="organization"
            value={selectedOrganization?.id ?? ''}
            options={organizationOptions}
            placeholder="Select an organization"
            helpText="Type to search by organization name."
            onChange={(_event, organizationIdValue) =>
              setSelectedOrganizationId(organizationIdValue || undefined)
            }
          />
        </Stack>

        {loading && !data ? (
          <Loader />
        ) : selectedOrganization ? (
          <OrganizationDetails
            key={selectedOrganization.id}
            canEdit={canEdit}
            organization={selectedOrganization}
            tenants={tenants}
          />
        ) : (
          <HeaderCard heading="Current organization">
            <Stack spacing={1} maxWidth={700}>
              <Typography variant="title-s">
                This tenant isn&apos;t part of an organization
              </Typography>
              <Typography variant="body-xs">
                Select an existing organization above to view its settings, or
                create a new organization and parent tenant.
              </Typography>
            </Stack>
          </HeaderCard>
        )}
      </Stack>

      {isCreateModalOpen && (
        <CreateOrganizationModal
          isOpen
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </SubpageLayout>
  );
}
